import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/hooks/useClerkAuth';
import TopNavBar from '@/components/TopNavBar';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import RoleManager from '@/components/RoleManager';
import { Users, ArrowLeft, Shield } from 'lucide-react';

const UserManagement = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { canManageRoles } = useAuth();

  useEffect(() => {
    if (!canManageRoles()) {
      navigate('/dashboard');
    }
  }, [canManageRoles, navigate]);

  if (!canManageRoles()) {
    return null;
  }

  return (
    <>
      <TopNavBar
        currentPage="users"
        onNavigateToAdmin={() => navigate('/?view=admin')}
        onNavigateToArticles={() => navigate('/')}
        onNavigateToDashboard={() => navigate('/dashboard')}
        onNavigateToQuestions={() => navigate('/questions')}
      />
      <div className="min-h-screen bg-gradient-to-br from-background to-learning-muted/20 p-6">
        <div className="container max-w-6xl mx-auto">
          {/* Header */}
          <div className="mb-8">
            <div className="flex items-center gap-4 mb-6">
              <Button
                variant="outline"
                size="sm"
                onClick={() => navigate('/dashboard')}
                className="bg-white/10 border-white/20"
              >
                <ArrowLeft className="h-4 w-4 mr-2" />
                {t('userManagement.backToDashboard')}
              </Button>
            </div>

            <div className="bg-gradient-hero rounded-lg p-6 text-primary-foreground mb-6">
              <div className="flex items-center gap-3 mb-2">
                <Shield className="h-8 w-8" />
                <h1 className="text-3xl font-bold">
                  {t('userManagement.title')}
                </h1>
              </div>
              <p className="text-primary-foreground/90">
                {t('userManagement.subtitle')}
              </p>
            </div>
          </div>

          {/* Admin Notice */}
          <Card className="mb-6 bg-amber-50 border-amber-200">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-amber-800">
                <Shield className="h-5 w-5" />
                Admin Access Required
              </CardTitle>
              <CardDescription className="text-amber-700">
                This page is restricted to administrators. You can manage user
                roles and permissions here.
              </CardDescription>
            </CardHeader>
            <CardContent className="text-amber-700">
              <div className="space-y-2 text-sm">
                <p>
                  <strong>Role Hierarchy:</strong>
                </p>
                <ul className="list-disc ml-5 space-y-1">
                  <li>
                    <strong>READER:</strong> Can read articles and practice
                    questions (default)
                  </li>
                  <li>
                    <strong>EDITOR:</strong> Can create and edit articles and
                    question catalogs
                  </li>
                  <li>
                    <strong>ADMIN:</strong> Can manage users, delete content,
                    and access all features
                  </li>
                </ul>
              </div>
            </CardContent>
          </Card>

          {/* Role Management */}
          <Card className="bg-white/95 backdrop-blur-sm border-0 shadow-card">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Users className="h-5 w-5" />
                Role Management
              </CardTitle>
              <CardDescription>
                Manage user roles and permissions for the application
              </CardDescription>
            </CardHeader>
            <CardContent>
              <RoleManager />
            </CardContent>
          </Card>
        </div>
      </div>
    </>
  );
};

export default UserManagement;
